import { AppItem, AppLinks, PortfolioData, PortfolioText } from '../types';

type SupportedLanguage = 'he' | 'en';

interface MainConfig {
  intro: string;
  text: string;
  app_version?: string;
  app_year?: string;
  apps: string[];
  email?: string;
  whatsapp?: string;
  github?: string;
  linkedin?: string;
  logoSize?: number;
  stack?: string[];
}

interface AppConfig {
  links?: Partial<AppLinks>;
  types?: string[];
  techs?: string[];
  strings?: Record<string, {
    name?: string;
  }>;
  icon?: string;
  screenshots?: string[];
}

const DATA_BASE_PATH = '/data';
const DEFAULT_IMAGE_EXTENSION = '.png';

export const createPortfolioText = (
  text: Partial<PortfolioText> = {},
): PortfolioText => new Proxy(text, {
  get: (target, key) => (
    typeof key === 'string'
      ? target[key as keyof PortfolioText] ?? ''
      : undefined
  ),
}) as PortfolioText;

const fetchJson = async <T,>(path: string): Promise<T> => {
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.statusText}`);
  }

  return response.json();
};

const fetchText = async (path: string): Promise<string> => {
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.statusText}`);
  }

  return response.text();
};

const withDefaultImageExtension = (name: string) => (
  /\.[a-z0-9]+$/i.test(name) ? name : `${name}${DEFAULT_IMAGE_EXTENSION}`
);

const LANGUAGE_FALLBACKS: Record<SupportedLanguage, string[]> = {
  he: ['he', 'de', 'en'],
  en: ['en', 'he', 'de'],
};

const getLanguageFallbacks = (lang: SupportedLanguage) => LANGUAGE_FALLBACKS[lang];

const fetchLocalizedText = async (
  basePath: string,
  fileName: string,
  lang: SupportedLanguage,
) => {
  const errors: unknown[] = [];

  for (const candidate of getLanguageFallbacks(lang)) {
    try {
      return await fetchText(`${basePath}/strings/${candidate}/${fileName}`);
    } catch (error) {
      errors.push(error);
    }
  }

  throw errors[0] ?? new Error(`Failed to load localized text: ${fileName}`);
};

const getLocalizedName = (appId: string, config: AppConfig, lang: SupportedLanguage) => {
  for (const candidate of getLanguageFallbacks(lang)) {
    const name = config.strings?.[candidate]?.name;

    if (name) {
      return name;
    }
  }

  return appId;
};

const getAppAssetPath = (
  appId: string,
  folder: 'icons' | 'screenshots',
  assetName: string,
) => `${DATA_BASE_PATH}/apps/${appId}/${folder}/${withDefaultImageExtension(assetName)}`;

const normalizeLinks = (links: Partial<AppLinks> = {}): AppLinks => ({
  appstore: links.appstore ?? '',
  playstore: links.playstore ?? '',
});

const loadApp = async (appId: string, lang: SupportedLanguage): Promise<AppItem> => {
  const appBasePath = `${DATA_BASE_PATH}/apps/${appId}`;
  const config = await fetchJson<AppConfig>(`${appBasePath}/config.json`);
  const description = await fetchLocalizedText(appBasePath, 'description.txt', lang);

  return {
    id: appId,
    name: getLocalizedName(appId, config, lang),
    links: normalizeLinks(config.links),
    types: config.types ?? [],
    description,
    screenshots: (config.screenshots ?? []).map(name => (
      getAppAssetPath(appId, 'screenshots', name)
    )),
    techs: config.techs ?? [],
    icon: config.icon ? getAppAssetPath(appId, 'icons', config.icon) : undefined,
  };
};

export const loadPortfolioData = async (lang: SupportedLanguage): Promise<PortfolioData> => {
  const config = await fetchJson<MainConfig>(`${DATA_BASE_PATH}/main/config.json`);
  const [intro, text, apps] = await Promise.all([
    fetchLocalizedText(`${DATA_BASE_PATH}/main`, config.intro, lang),
    fetchJson<Partial<PortfolioText>>(`${DATA_BASE_PATH}/main/strings/${lang}/${config.text}`),
    Promise.all(config.apps.map(appId => loadApp(appId, lang))),
  ]);

  return {
    intro,
    text: createPortfolioText(text),
    appVersion: config.app_version ?? '',
    appYear: config.app_year ?? '',
    logoSize: config.logoSize ?? 64,
    stack: config.stack ?? [],
    apps,
    email: config.email,
    whatsapp: config.whatsapp,
    github: config.github,
    linkedin: config.linkedin,
  };
};
